import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { FormsModule } from '@angular/forms';
import { filtermodels } from '../../../../../model/sort-filter';

export interface FilterData {
  columns: string[];
  headerMap: Map<string, any>;
  defaultLogDate?: string;
  currentFilters?: filtermodels; // Add current filters to interface - TK 01/15/2025
}

@Component({
  selector: 'app-filter-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    FormsModule
  ],
  template: `
    <h2 mat-dialog-title>Filter Application Logs</h2>
    <mat-dialog-content>
      <div class="filter-container">
        <div *ngFor="let column of data.columns" class="filter-row">
          <!-- Date fields with date picker -->
          <div *ngIf="isDateField(column)" class="date-filter-container">
            <mat-form-field appearance="outline" class="filter-field">
              <mat-label>{{ data.headerMap.get(column).label }} (From)</mat-label>
              <input
                matInput
                [matDatepicker]="pickerFrom"
                [placeholder]="'From date'"
                [(ngModel)]="dateFilters[column].from"
              />
              <mat-datepicker-toggle matSuffix [for]="pickerFrom"></mat-datepicker-toggle>
              <mat-datepicker #pickerFrom></mat-datepicker>
            </mat-form-field>

            <mat-form-field appearance="outline" class="filter-field">
              <mat-label>{{ data.headerMap.get(column).label }} (To)</mat-label>
              <input
                matInput
                [matDatepicker]="pickerTo"
                [placeholder]="'To date'"
                [(ngModel)]="dateFilters[column].to"
              />
              <mat-datepicker-toggle matSuffix [for]="pickerTo"></mat-datepicker-toggle>
              <mat-datepicker #pickerTo></mat-datepicker>
            </mat-form-field>
          </div>

          <!-- Text fields -->
          <mat-form-field *ngIf="!isDateField(column)" appearance="outline" class="filter-field">
            <mat-label>{{ data.headerMap.get(column).label }}</mat-label>
            <input
              matInput
              [placeholder]="'Filter ' + data.headerMap.get(column).label"
              [(ngModel)]="filters[column]"
            />
          </mat-form-field>
        </div>
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()">Cancel</button>
      <button mat-button (click)="onClear()">Clear All</button>
      <button mat-raised-button color="primary" (click)="onApply()">Apply Filters</button>
    </mat-dialog-actions>
  `,
      styles: [`
    .filter-container {
      max-height: 400px;
      overflow-y: auto;
      padding: 16px 0;
    }

    .filter-row {
      margin-bottom: 16px;
    }

    .filter-field {
      width: 100%;
    }

    .date-filter-container {
      display: flex;
      gap: 16px;
      align-items: flex-start;
    }

    .date-filter-container .filter-field {
      flex: 1;
    }

    mat-dialog-actions {
      padding: 16px 0;
    }

    mat-dialog-content {
      padding: 0 24px;
    }

    mat-dialog-title {
      padding: 24px 24px 0 24px;
    }

    mat-dialog-actions {
      padding: 16px 24px;
    }
  `]
})
export class FilterDialogComponent {
  filters: { [key: string]: string } = {};
  dateFilters: { [key: string]: { from: Date | null; to: Date | null } } = {};

  constructor(
    public dialogRef: MatDialogRef<FilterDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FilterData
  ) {
    // Initialize filters object - TK 01/15/2025
    this.data.columns.forEach(col => {
      this.filters[col] = '';

      // Initialize date filters for date columns
      if (this.isDateField(col)) {
        this.dateFilters[col] = { from: null, to: null };

        // Set default date for logDate column if provided - TK 01/15/2025
        if (col === 'logDate' && this.data.defaultLogDate) {
          this.dateFilters[col].from = new Date(this.data.defaultLogDate);
        }
      }
    });

    // Initialize with current filters if provided - TK 01/15/2025
    if (this.data.currentFilters) {
      Object.keys(this.data.currentFilters).forEach(col => {
        const filter = this.data.currentFilters![col];

        if (filter.filterType === 'date') {
          // Handle date filters
          if (filter.dateFrom) {
            this.dateFilters[col] = {
              from: new Date(filter.dateFrom),
              to: filter.dateTo ? new Date(filter.dateTo) : null
            };
          }
        } else {
          // Handle text filters
          this.filters[col] = filter.filter || '';
        }
      });
    }
  }

  isDateField(column: string): boolean {
    const header = this.data.headerMap.get(column);
    return header.type === 'date';
  }

  getInputType(column: string): string {
    const header = this.data.headerMap.get(column);
    return header.type === 'date' ? 'date' : 'text';
  }

  onCancel(): void {
    this.dialogRef.close();
  }

           onClear(): void {
           this.data.columns.forEach(col => {
             this.filters[col] = '';
             if (this.isDateField(col)) {
               this.dateFilters[col] = { from: null, to: null };
             }
           });

           // Close dialog with empty result to indicate clear - TK 01/15/2025
           this.dialogRef.close({});
         }

  onApply(): void {
    // Filter out empty values - TK 01/15/2025
    const activeFilters: { [key: string]: any } = {};

    Object.keys(this.filters).forEach(key => {
      if (this.filters[key] && this.filters[key].trim() !== '') {
        activeFilters[key] = this.filters[key];
      }
    });

    // Add date filters - TK 01/15/2025
    Object.keys(this.dateFilters).forEach(key => {
      const dateFilter = this.dateFilters[key];
      if (dateFilter.from || dateFilter.to) {
        activeFilters[key] = {
          from: dateFilter.from,
          to: dateFilter.to
        };
      }
    });

    this.dialogRef.close(activeFilters);
  }
}
